import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Space, Modal, Select, Checkbox, Radio, InputNumber, Switch, Card, Row, Col, Tooltip, Steps, DatePicker, Table, Tag, Typography, Divider, message } from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  EditOutlined, 
  EyeOutlined, 
  ArrowUpOutlined, 
  ArrowDownOutlined,
  FormOutlined,
  FileTextOutlined,
  CheckSquareOutlined,
  CheckCircleOutlined,
  NumberOutlined,
  DownOutlined,
  CalendarOutlined,
  FileOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text, Paragraph } = Typography;

// Field types
const FIELD_TYPES = {
  INPUT: 'input',
  TEXTAREA: 'textarea',
  CHECKBOX: 'checkbox',
  RADIO: 'radio',
  NUMBER: 'number',
  SELECT: 'select',
  DATE: 'date',
  FORM_BREAK: 'formBreak',
};

// Initial field structure
const getInitialField = () => ({
  id: uuidv4(),
  type: FIELD_TYPES.INPUT,
  label: '',
  placeholder: '',
  required: false,
  instructions: '',
  options: ['Option 1'],
  minValue: 0,
  maxValue: 100,
  heading: '',
  description: '',
});

const AntdElementsForm = (props) => {
  const {
    initialFields = [],
    onFieldsChange,
    displayMode = 'editor',
    onFormSubmit,
    className,
    submitting,
    ...restProps
  } = props;
  
  const [fields, setFields] = useState(initialFields);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [currentField, setCurrentField] = useState(getInitialField());
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [previewForm] = Form.useForm();
  const [localPreview, setLocalPreview] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [firstPageConfig, setFirstPageConfig] = useState({ heading: 'Form', description: '' });
  const [isFirstPageModalVisible, setIsFirstPageModalVisible] = useState(false);
  const [firstPageForm] = Form.useForm();

  // Update fields when initialFields change
  useEffect(() => {
    setFields(initialFields);
  }, [initialFields]);

  // Add effect to save first page config to local storage
  useEffect(() => {
    // Save first page config to local storage when it changes
    localStorage.setItem('formFirstPageConfig', JSON.stringify(firstPageConfig));
  }, [firstPageConfig]);

  // Load first page config from local storage on initial load
  useEffect(() => {
    const savedConfig = localStorage.getItem('formFirstPageConfig');
    if (savedConfig) {
      try {
        setFirstPageConfig(JSON.parse(savedConfig));
      } catch (e) {
        console.error('Error parsing saved form config:', e);
      }
    }
  }, []);

  // Display either the passed displayMode prop or local preview state
  const showPreview = displayMode === 'preview' || localPreview;

  // Update fields and trigger onFieldsChange
  const updateFields = (newFields) => {
    setFields(newFields);
    if (onFieldsChange) {
      onFieldsChange(newFields);
    }
  };

  // Add a new field
  const handleAddField = () => {
    form.validateFields()
      .then((values) => {
        const newField = { ...currentField, ...values };
        
        if (isEditing) {
          const updatedFields = fields.map(field => 
            field.id === newField.id ? newField : field
          );
          updateFields(updatedFields);
        } else {
          updateFields([...fields, newField]);
        }
        
        setIsModalVisible(false);
        setCurrentField(getInitialField());
        form.resetFields();
        setIsEditing(false);
      })
      .catch((errorInfo) => {
        console.log('Validation failed:', errorInfo);
      });
  };

  // Delete a field
  const handleDeleteField = (id) => {
    const updatedFields = fields.filter(field => field.id !== id);
    updateFields(updatedFields);
  };

  // Edit a field
  const handleEditField = (field) => {
    setCurrentField(field);
    setIsEditing(true);
    setIsModalVisible(true);
    form.setFieldsValue(field);
  };

  // Move field up
  const handleMoveUp = (index) => {
    if (index === 0) return;
    const newFields = [...fields];
    [newFields[index], newFields[index - 1]] = [newFields[index - 1], newFields[index]];
    updateFields(newFields);
  };

  // Move field down
  const handleMoveDown = (index) => {
    if (index === fields.length - 1) return;
    const newFields = [...fields];
    [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
    updateFields(newFields);
  };

  // Handle option changes for multi-choice fields
  const handleOptionChange = (options) => {
    setCurrentField({ ...currentField, options });
  };

  // Add a new option
  const handleAddOption = () => {
    const options = [...currentField.options, `Option ${currentField.options.length + 1}`];
    handleOptionChange(options);
  };

  // Remove an option
  const handleRemoveOption = (index) => {
    const options = currentField.options.filter((_, i) => i !== index);
    handleOptionChange(options);
  };

  // Handle option text change
  const handleOptionTextChange = (index, value) => {
    const options = [...currentField.options];
    options[index] = value;
    handleOptionChange(options);
  };

  // Handle preview form submission
  const handlePreviewSubmit = (values) => {
    console.log('Form submitted with values:', values);
    if (onFormSubmit) {
      onFormSubmit(values);
      // Clear form after successful submission
      previewForm.resetFields();
      setCurrentStep(0);
      message.success('Form submitted successfully!');
    }
  };

  // Handle form submission with validation
  const handleSubmitWithValidation = () => {
    previewForm.validateFields()
      .then(values => {
        handlePreviewSubmit(values);
      })
      .catch(error => {
        // Find which page contains the field with error
        const errorFieldNames = error.errorFields.map(f => f.name[0]);
        
        if (errorFieldNames.length > 0) {
          // Find which page contains these fields
          const pages = getFormPages();
          let errorPageIndex = -1;
          
          for (let i = 0; i < pages.length; i++) {
            for (const fieldName of errorFieldNames) {
              const fieldExists = pages[i].fields.some(field => field.id === fieldName);
              if (fieldExists) {
                errorPageIndex = i;
                break;
              }
            }
            if (errorPageIndex !== -1) break;
          }
          
          if (errorPageIndex !== -1) {
            setCurrentStep(errorPageIndex);
            message.error(`Please fill out all required fields on page ${errorPageIndex + 1}`);
          } else {
            message.error('Please fill out all required fields');
          }
        }
      });
  };

  // Handle first page configuration
  const handleFirstPageConfig = () => {
    firstPageForm.validateFields().then(values => {
      // Make a deep copy to ensure we're not referencing the same object
      const newConfig = JSON.parse(JSON.stringify(values));
      setFirstPageConfig(newConfig);
      setIsFirstPageModalVisible(false);
      
      // Force update the UI
      const updatedFields = [...fields];
      updateFields(updatedFields);
      
      // Save to localStorage immediately
      localStorage.setItem('formFirstPageConfig', JSON.stringify(newConfig));
    }).catch(err => console.log('Validation failed:', err));
  };

  // Get form pages based on form breaks
  const getFormPages = () => {
    const pages = [];
    let currentPage = [];
    
    // Add first page with first page config
    pages.push({
      fields: [],
      heading: firstPageConfig.heading,
      description: firstPageConfig.description
    });
    
    // Process all fields
    fields.forEach(field => {
      if (field.type === FIELD_TYPES.FORM_BREAK) {
        // Add all fields collected so far to the current page
        pages[pages.length - 1].fields = currentPage;
        // Create a new page with this form break's heading and description
        pages.push({
          fields: [],
          heading: field.heading,
          description: field.description
        });
        currentPage = [];
      } else {
        currentPage.push(field);
      }
    });
    
    // Add the remaining fields to the last page
    if (pages.length > 0) {
      pages[pages.length - 1].fields = currentPage;
    }
    
    return pages;
  };

  // Get steps for the Steps component
  const getSteps = () => {
    const pages = getFormPages();
    return pages.map((page, index) => ({
      title: page.heading || `Page ${index + 1}`,
      description: page.description ? page.description.substring(0, 20) + '...' : '',
    }));
  };

  // Handle next page in multi-page form with validation
  const handleNextPage = () => {
    // Get fields for current page
    const pages = getFormPages();
    const currentPageFields = pages[currentStep].fields;
    
    // Get all field names on this page that need validation
    const fieldsToValidate = currentPageFields
      .filter(field => field.required)
      .map(field => field.id);
    
    if (fieldsToValidate.length > 0) {
      // Validate only the required fields on the current page
      previewForm
        .validateFields(fieldsToValidate)
        .then(() => {
          setCurrentStep(currentStep + 1);
        })
        .catch(error => {
          // More specific error message showing which fields are missing
          const missingFields = error.errorFields.map(f => f.name[0]).join(', ');
          message.error(`Please complete all required fields: ${missingFields}`);
        });
    } else {
      // No required fields on this page, proceed directly
      setCurrentStep(currentStep + 1);
    }
  };

  // Handle previous page
  const handlePrevPage = () => {
    setCurrentStep(currentStep - 1);
  };

  // Render field in preview mode
  const renderPreviewField = (field) => {
    // Skip rendering form breaks in preview mode
    if (field.type === FIELD_TYPES.FORM_BREAK) {
      return null;
    }
    
    const { type, label, placeholder, required, instructions, options, minValue, maxValue } = field;
    
    // Improved form item props with better tooltip styling
    const formItemProps = {
      label: (
        <span>
          {label}
          {instructions && (
            <Tooltip title={instructions}>
              <QuestionCircleOutlined style={{ marginLeft: 8, color: '#1890ff' }} />
            </Tooltip>
          )}
        </span>
      ),
      name: field.id,
      required: required,
      rules: required ? [{ required: true, message: `Please fill in ${label}` }] : [],
    };

    switch (type) {
      case FIELD_TYPES.INPUT:
        return (
          <Form.Item {...formItemProps}>
            <Input placeholder={placeholder} />
          </Form.Item>
        );
      case FIELD_TYPES.TEXTAREA:
        return (
          <Form.Item {...formItemProps}>
            <TextArea placeholder={placeholder} rows={4} />
          </Form.Item>
        );
      case FIELD_TYPES.CHECKBOX:
        return (
          <Form.Item {...formItemProps} valuePropName="checked">
            <Checkbox.Group>
              {options.map((option, index) => (
                <Checkbox key={index} value={option}>{option}</Checkbox>
              ))}
            </Checkbox.Group>
          </Form.Item>
        );
      case FIELD_TYPES.RADIO:
        return (
          <Form.Item {...formItemProps}>
            <Radio.Group>
              {options.map((option, index) => (
                <Radio key={index} value={option}>{option}</Radio>
              ))}
            </Radio.Group>
          </Form.Item>
        );
      case FIELD_TYPES.NUMBER:
        return (
          <Form.Item {...formItemProps}>
            <InputNumber 
              placeholder={placeholder} 
              min={minValue} 
              max={maxValue} 
              style={{ width: '100%' }}
            />
          </Form.Item>
        );
      case FIELD_TYPES.SELECT:
        return (
          <Form.Item {...formItemProps}>
            <Select 
              placeholder={placeholder} 
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              style={{ width: '100%' }}
            >
              {options.map((option, index) => (
                <Option key={index} value={option}>{option}</Option>
              ))}
            </Select>
          </Form.Item>
        );
      case FIELD_TYPES.DATE:
        return (
          <Form.Item {...formItemProps}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  // If in preview mode, render the multi-page form with the configured fields
  if (showPreview) {
    const pages = getFormPages();
    const steps = getSteps();
    
    return (
      <div className={className} {...restProps}>
        <Card>
          {pages.length > 1 && (
            <Steps 
              current={currentStep} 
              style={{ marginBottom: 24 }}
            >
              {steps.map((step, index) => (
                <Steps.Step 
                  key={index} 
                  title={step.title} 
                  description={step.description}
                />
              ))}
            </Steps>
          )}
          
          <Form
            form={previewForm}
            layout="vertical"
            onFinish={handlePreviewSubmit}
          >
            {/* Improved page header with better styling */}
            {pages[currentStep].heading && (
              <div style={{ marginBottom: 24, borderBottom: '1px solid #f0f0f0', paddingBottom: 16 }}>
                <Title level={3} style={{ marginBottom: 8 }}>{pages[currentStep].heading}</Title>
                {pages[currentStep].description && (
                  <Paragraph style={{ fontSize: 16, color: '#666' }}>{pages[currentStep].description}</Paragraph>
                )}
              </div>
            )}
            
            {pages[currentStep].fields.map((field) => (
              <div key={field.id}>
                {renderPreviewField(field)}
              </div>
            ))}
            
            <Form.Item style={{ marginTop: 24 }}>
              <Space>
                {currentStep > 0 && (
                  <Button onClick={handlePrevPage} size="large">
                    Previous
                  </Button>
                )}
                
                {currentStep < pages.length - 1 ? (
                  <Button type="primary" onClick={handleNextPage} size="large">
                    Next
                  </Button>
                ) : (
                  <Button type="primary" onClick={handleSubmitWithValidation} loading={submitting} size="large">
                    Submit
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  // Otherwise, render the form builder
  return (
    <div className={className} {...restProps}>
      <Card>
        <Space style={{ marginBottom: 16 }}>
          <Button 
            type="primary" 
            onClick={() => {
              setIsModalVisible(true);
              setIsEditing(false);
              form.resetFields();
              setCurrentField(getInitialField());
            }}
          >
            Add Field
          </Button>
          <Button 
            onClick={() => {
              setLocalPreview(!localPreview);
              // Reset to first page when toggling preview
              setCurrentStep(0);
              // Reset form when entering preview
              if (!localPreview) {
                previewForm.resetFields();
              }
            }}
          >
            {localPreview ? 'Edit Form' : 'Preview Form'}
          </Button>
        </Space>

        <Table 
          dataSource={[
            {
              id: 'first-page',
              type: FIELD_TYPES.FORM_BREAK,
              heading: firstPageConfig.heading,
              description: firstPageConfig.description,
              isFirstPage: true
            },
            ...fields
          ]} 
          columns={[
            {
              title: 'Type',
              dataIndex: 'type',
              key: 'type',
              width: 150,
              render: (type, record) => {
                let icon;
                
                switch(type) {
                  case FIELD_TYPES.TEXTAREA: 
                    icon = <FileTextOutlined />;
                    break;
                  case FIELD_TYPES.CHECKBOX: 
                    icon = <CheckSquareOutlined />;
                    break;
                  case FIELD_TYPES.RADIO: 
                    icon = <CheckCircleOutlined />;
                    break;
                  case FIELD_TYPES.NUMBER: 
                    icon = <NumberOutlined />;
                    break;
                  case FIELD_TYPES.SELECT: 
                    icon = <DownOutlined />;
                    break;
                  case FIELD_TYPES.DATE: 
                    icon = <CalendarOutlined />;
                    break;
                  case FIELD_TYPES.FORM_BREAK: 
                    icon = <FileOutlined />;
                    break;
                  default:
                    icon = <FormOutlined />;
                }
                
                return (
                  <Space>
                    {icon}
                    {record.isFirstPage ? 'First Page' : type}
                  </Space>
                );
              }
            },
            {
              title: 'Label/Heading',
              dataIndex: 'label',
              key: 'label',
              render: (text, record) => 
                record.type === FIELD_TYPES.FORM_BREAK ? record.heading : text
            },
            {
              title: 'Required',
              dataIndex: 'required',
              key: 'required',
              width: 100,
              render: (required, record) => 
                record.type === FIELD_TYPES.FORM_BREAK ? 
                  '-' : 
                  required ? 'Yes' : 'No'
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 200,
              render: (text, record, index) => {
                if (record.isFirstPage) {
                  return (
                    <Button 
                      icon={<EditOutlined />} 
                      onClick={() => {
                        setIsFirstPageModalVisible(true);
                        firstPageForm.setFieldsValue(firstPageConfig);
                      }}
                      size="small"
                    >
                      Edit
                    </Button>
                  );
                }
                
                return (
                  <Space>
                    <Button 
                      icon={<ArrowUpOutlined />} 
                      disabled={index === 1}
                      onClick={() => handleMoveUp(index - 1)}
                      size="small"
                    />
                    <Button 
                      icon={<ArrowDownOutlined />} 
                      disabled={index === fields.length}
                      onClick={() => handleMoveDown(index - 1)}
                      size="small"
                    />
                    <Button 
                      icon={<EditOutlined />} 
                      onClick={() => handleEditField(record)}
                      size="small"
                    />
                    <Button 
                      danger
                      icon={<DeleteOutlined />} 
                      onClick={() => handleDeleteField(record.id)}
                      size="small"
                    />
                  </Space>
                );
              }
            }
          ]}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* Field Modal */}
      <Modal
        title={isEditing ? "Edit Field" : "Add Field"}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleAddField}>
            {isEditing ? "Update" : "Add"}
          </Button>
        ]}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={currentField}
        >
          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                name="type"
                label="Field Type"
                rules={[{ required: true, message: 'Please select a field type' }]}
              >
                <Select 
                  onChange={(value) => setCurrentField({ ...currentField, type: value })}
                  showSearch
                >
                  <Option value={FIELD_TYPES.INPUT}>Text Input</Option>
                  <Option value={FIELD_TYPES.TEXTAREA}>Text Area</Option>
                  <Option value={FIELD_TYPES.CHECKBOX}>Multiple Choice (Checkbox)</Option>
                  <Option value={FIELD_TYPES.RADIO}>Radio Buttons</Option>
                  <Option value={FIELD_TYPES.NUMBER}>Number</Option>
                  <Option value={FIELD_TYPES.SELECT}>Dropdown Select</Option>
                  <Option value={FIELD_TYPES.DATE}>Date Picker</Option>
                  <Option value={FIELD_TYPES.FORM_BREAK}>Form Page Break</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              {currentField.type !== FIELD_TYPES.FORM_BREAK ? (
                <Form.Item
                  name="label"
                  label="Field Label"
                  rules={[{ required: true, message: 'Please enter a label' }]}
                >
                  <Input placeholder="Enter field label" />
                </Form.Item>
              ) : (
                <Form.Item
                  name="heading"
                  label="Page Heading"
                  rules={[{ required: true, message: 'Please enter a page heading' }]}
                >
                  <Input placeholder="Enter page heading" />
                </Form.Item>
              )}
            </Col>
          </Row>

          {currentField.type !== FIELD_TYPES.FORM_BREAK ? (
            <>
              <Row gutter={24}>
                <Col span={12}>
                  <Form.Item name="placeholder" label="Placeholder">
                    <Input placeholder="Enter placeholder text" />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="instructions" label="Instructions">
                    <Input placeholder="Enter instructions for this field" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="required" valuePropName="checked">
                <Switch checkedChildren="Required" unCheckedChildren="Optional" />
              </Form.Item>

              {(currentField.type === FIELD_TYPES.NUMBER) && (
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item name="minValue" label="Minimum Value">
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="maxValue" label="Maximum Value">
                      <InputNumber style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {(currentField.type === FIELD_TYPES.CHECKBOX || 
                currentField.type === FIELD_TYPES.RADIO || 
                currentField.type === FIELD_TYPES.SELECT) && (
                <>
                  <Divider>Options</Divider>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
                    <Button onClick={handleAddOption} icon={<PlusOutlined />}>
                      Add Option
                    </Button>
                  </div>
                  {currentField.options.map((option, index) => (
                    <Row key={index} style={{ marginBottom: '8px' }}>
                      <Col flex="auto">
                        <Input
                          value={option}
                          onChange={(e) => handleOptionTextChange(index, e.target.value)}
                          placeholder={`Option ${index + 1}`}
                        />
                      </Col>
                      <Col flex="none" style={{ marginLeft: '8px' }}>
                        <Button
                          icon={<DeleteOutlined />}
                          onClick={() => handleRemoveOption(index)}
                          disabled={currentField.options.length <= 1}
                        />
                      </Col>
                    </Row>
                  ))}
                </>
              )}
            </>
          ) : (
            <Form.Item name="description" label="Page Description">
              <TextArea placeholder="Enter description for this page" rows={4} />
            </Form.Item>
          )}
        </Form>
      </Modal>

      {/* First Page Modal */}
      <Modal
        title="Configure First Page"
        open={isFirstPageModalVisible}
        onCancel={() => setIsFirstPageModalVisible(false)}
        footer={[
          <Button key="cancel" onClick={() => setIsFirstPageModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleFirstPageConfig}>
            Save
          </Button>
        ]}
        width={600}
      >
        <Form
          form={firstPageForm}
          layout="vertical"
          initialValues={firstPageConfig}
        >
          <Form.Item
            name="heading"
            label="Page Heading"
            rules={[{ required: true, message: 'Please enter a page heading' }]}
          >
            <Input placeholder="Enter page heading" />
          </Form.Item>
          <Form.Item
            name="description"
            label="Page Description"
          >
            <TextArea placeholder="Enter description for this page" rows={4} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AntdElementsForm;


